import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { queryClient } from "~/entry.client";
import { type EpisodeCard, getOrderPinnedEpisodeAtFirst, resolveEpisodeCardForSeries } from "~/lib/seriesDashboard";
import type { GetAllDevotionalSeriesForDashboardResult } from "~/sanity/sanity.types";
import type { SeriesProgressResponse } from "~/services/progress.client";
import type { Page } from "~/stores/reactQuery/usePagesQuery";
import type { User } from "~/types/user";

import { getSeriesProgressQueryOptions } from "./useSeriesProgress";

type PageSeriesProps = {
  series: GetAllDevotionalSeriesForDashboardResult;
  pinnedSeriesIds?: Array<string>;
  initialProgressSeriesIds?: Array<string>;
  enableProgress?: boolean;
  hasDevoPlus?: boolean;
  page: Page;
  user: User | null;
};

type EpisodeCardLocal = EpisodeCard;

type DashboardSeriesItem = GetAllDevotionalSeriesForDashboardResult[number] & {
  progress?: SeriesProgressResponse | null;
  episodeCard?: EpisodeCardLocal | null;
};

type DashboardSeriesReturn = {
  series: Array<DashboardSeriesItem>;
  isLoading: boolean;
  isFetching: boolean;
};

const SERIES_EPISODE_CARD_QUERY_KEY = {
  all: ["series-episode-cards"],
  byPage: (page: Page, today: string, userId: number | "guest", progressVersion: Array<number>) => [
    ...SERIES_EPISODE_CARD_QUERY_KEY.all,
    page,
    today,
    userId,
    ...progressVersion,
  ],
};

export function usePageSeries({
  series,
  pinnedSeriesIds,
  initialProgressSeriesIds,
  enableProgress = true,
  user,
  hasDevoPlus,
  page,
}: PageSeriesProps): DashboardSeriesReturn {
  const orderedSeries = getOrderPinnedEpisodeAtFirst(series, pinnedSeriesIds);
  const initialProgressScope = initialProgressSeriesIds ? new Set(initialProgressSeriesIds) : null;

  // Get today's date in the device's local timezone (YYYY-MM-DD format)
  const todayDate = dayjs().format("YYYY-MM-DD");

  const progressQueries = useQueries({
    queries: orderedSeries.map((s) => ({
      ...getSeriesProgressQueryOptions(s._id),
      enabled:
        enableProgress &&
        Boolean(s._id) &&
        Boolean(user?.id) &&
        (initialProgressScope ? initialProgressScope.has(s._id) : true),
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: keepPreviousData,
    })),
  });

  const isLoadingProgress = enableProgress && progressQueries.some((q) => q.isLoading);
  const isFetchingProgress = enableProgress && progressQueries.some((q) => q.isFetching);
  const progressVersion = enableProgress ? progressQueries.map((q) => q.dataUpdatedAt) : [];

  // 2) Add progress to series
  const seriesWithProgress: Array<DashboardSeriesItem> = orderedSeries.map((s, index) => ({
    ...s,
    progress: enableProgress ? (progressQueries[index]?.data ?? null) : null,
  }));

  const {
    data: episodeCardsBySeriesId,
    isLoading: isLoadingCards,
    isFetching: isFetchingCards,
  } = useQuery({
    queryKey: SERIES_EPISODE_CARD_QUERY_KEY.byPage(page, todayDate, user?.id ?? "guest", progressVersion),
    enabled: !isLoadingProgress,
    queryFn: async () => {
      const entries = await Promise.all(
        seriesWithProgress.map(async (s) => {
          const card = await resolveEpisodeCardForSeries(s, hasDevoPlus);
          return [s._id, card] as const;
        }),
      );
      const map: Record<string, EpisodeCardLocal | null> = {};
      for (const [id, card] of entries) map[id] = card;
      return map;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });

  const composed: Array<DashboardSeriesItem> = seriesWithProgress.map((s) => ({
    ...s,
    episodeCard: episodeCardsBySeriesId?.[s._id] ?? null,
  }));

  return {
    series: composed,
    isLoading: Boolean(isLoadingProgress || isLoadingCards),
    isFetching: Boolean(isFetchingProgress || isFetchingCards),
  };
}

/**
 * Optimistically updates all episode card queries with current progress
 * @param cmsId - The CMS ID of the episode
 * @param progressPercentage - The current progress percentage
 * @param completedAtUtc - The UTC timestamp when the episode was completed (only when 100%)
 * @param lastListenedUtc - The UTC timestamp when the episode was last listened to
 */
export function optimisticallyUpdateEpisodeCardsQueries(
  cmsId: string,
  progressPercentage: number,
  completedAtUtc?: string,
  lastListenedUtc?: string,
): void {
  queryClient.setQueriesData<Record<string, EpisodeCard | null>>(
    { queryKey: SERIES_EPISODE_CARD_QUERY_KEY.all },
    (oldData) => {
      if (!oldData) return oldData;

      const updatedData: Record<string, EpisodeCard | null> = {};
      for (const [seriesId, card] of Object.entries(oldData)) {
        if (card && card.episodeId === cmsId) {
          updatedData[seriesId] = {
            ...card,
            episodeProgressPercentage: progressPercentage,
            episodeCompletedAtUtc: completedAtUtc,
            episodeLastListenedUtc: lastListenedUtc,
          };
        } else {
          updatedData[seriesId] = card;
        }
      }
      return updatedData;
    },
  );
}

/**
 * Resets all episode card queries to force re-computation of the "Next Episode".
 * Uses resetQueries instead of invalidateQueries because the query has refetchOnMount: false,
 * which would prevent stale queries from refetching on mount.
 * Should be called when an episode is completed to ensure the dashboard shows the correct next episode.
 */
export function invalidateEpisodeCardsQueries(): void {
  void queryClient.resetQueries({ queryKey: SERIES_EPISODE_CARD_QUERY_KEY.all });
}
